import { CONTRACTOR, PROJECT } from "../_constants/actionConstants";
import axios from "axios";
import { BASE_URL } from "../_constants/urlConstants";
import raiseError from "./raiseError";
import { DOCTYPE } from "../_constants/docTypeConstants";
import qs from "qs";
import raiseWarning from "./raiseWarning";

const add = (projDto) => async (dispatch) => {
  dispatch({ type: PROJECT.ADD });
  const apiEndpoint = `${BASE_URL}/api/Project`;
  try {
    const res = await axios.post(apiEndpoint, projDto);
    if (res.data.message) {
      dispatch({ type: PROJECT.ADD_REJECTED });
      raiseWarning(PROJECT.ADD_REJECTED, res.data.message);
    } else {
      dispatch({ type: PROJECT.ADD_FULFILLED });
    }
  } catch (err) {
    raiseError(PROJECT.ADD_REJECTED, err.response.data);
  }
};

const activate = (projId) => async (dispatch) => {
  dispatch({ type: PROJECT.INITIATE });
  const apiEndpoint = `${BASE_URL}/api/Project/${projId}/Activate`;
  try {
    await axios.patch(apiEndpoint);
    dispatch({ type: PROJECT.INITIATE_FULFILLED });
    return;
  } catch (err) {
    raiseError(PROJECT.INITIATE_REJECTED, err.response.data);
  }
};

/**
 * Re-fetch all open ProjectLists that currently have the same dist filter applied.
 * @param {number} district - required
 */
const refetchAllOpenProjectLists = (district) => async (dispatch, getState) => {
  // Before fetching the list, first check if any tabs need updating. Return if none.
  const tabsToUpdate = getState()
    .mainTabs.openTabs.filter(
      (t) => t.docType === DOCTYPE.PROJECT_LIST && t.metadata.dist === district
    )
    .map((t) => t.tabId);
  if (tabsToUpdate.length === 0) return;

  dispatch({ type: PROJECT.FETCH_ALL });
  // ProjectList only needs to be updated by the district filter
  const filters = { ...emptyProjectFilters, district };
  const apiEndpoint_projList = `${BASE_URL}/api/Project/List?${qs.stringify(filters)}`;
  const projListRes = await axios.get(apiEndpoint_projList);
  Object.keys(getState().projects.openProjectLists).forEach((tabId) => {
    if (tabsToUpdate.includes(+tabId)) {
      dispatch({
        type: PROJECT.FETCH_ALL_FULFILLED,
        tabId,
        payload: projListRes.data,
      });
    }
  });
};

const update = (revProjectData) => async (dispatch) => {
  dispatch({ type: PROJECT.EDIT });
  const apiEndpoint = `${BASE_URL}/api/Project`;
  try {
    const res = await axios.put(apiEndpoint, revProjectData);
    if (res.data.message) {
      dispatch({ type: PROJECT.EDIT_REJECTED });
      raiseWarning(PROJECT.EDIT_REJECTED, res.data.message);
    } else {
      dispatch({
        type: PROJECT.EDIT_FULFILLED,
        projectId: revProjectData.projectId,
        projDetails: res.data,
      });
      return res.data;
    }
  } catch (err) {
    raiseError(PROJECT.EDIT_REJECTED, err.response.data);
  }
};

/**
 * Fetches
 * @param {ProjectFilters} filters
 * @param {number?} tabId
 * @returns
 */
const fetchAllByFilter =
  (filters, tabId = null) =>
  async (dispatch) => {
    dispatch({ type: PROJECT.FETCH_ALL });
    const apiEndpoint = `${BASE_URL}/api/Project/List?${qs.stringify(filters)}`;
    try {
      const res = await axios.get(apiEndpoint);
      dispatch({
        type: PROJECT.FETCH_ALL_FULFILLED,
        payload: res.data,
        tabId,
      });
      return res.data;
    } catch (err) {
      raiseError(PROJECT.FETCH_ALL_REJECTED, err.response.data);
    }
  };

/**
 * Fetches project list. Used for both "browsing" and "searching".
 * Unlike userActions, we do not have a separate fetch for autocomplete
 * suggestions, since the search is only on EA and it's a simple
 * search for substring.
 *
 * @param {number} tabId - required
 * @param {pageNo} pageNo - required
 * @param {pageSize} pageSize - required
 * @param {Boolean} resetList - required
 * @param {ProjectFilters} filters - optional
 * @returns
 */
const fetchAllPaged =
  (tabId, pageNo, pageSize, resetList, filters) => async (dispatch, getState) => {
    dispatch({ type: PROJECT.FETCH_ALL });

    // Set default filters if none provided
    filters = filters || emptyProjectFilters;

    // Below is used for caching, not for the actual search. The string
    // represents the state of the filter, and can be used to detect
    // if the filter is changed.
    const combinedSearchString = filters.EA
      ? `${filters.proDMSProjectsOnly ? "1" : "0"}-${filters.district || "0"}-${
          filters.EA
        }`
      : "";

    // If history of the same search exists, load the cached results instead of
    // fetching anew. BUT, if the requested pageNo is > 1, do not load from
    // the cache since it's a new request.
    const miscSessionData = getState().projects.miscSessionData;
    if (
      pageNo === 1 &&
      tabId in miscSessionData &&
      miscSessionData[tabId].projectSearchHistory &&
      combinedSearchString in miscSessionData[tabId].projectSearchHistory
    ) {
      dispatch({
        type: PROJECT.FETCH_ALL_PAGED_FULFILLED,
        tabId,
        resetList,
        projectList: miscSessionData[tabId].projectSearchHistory[combinedSearchString],
      });
      return;
    }
    // If EA is a non-empty string, treat this fetch as a "search"
    if (filters.EA)
      dispatch({
        type: PROJECT.SET_PROJECT_WAITINGFOR,
        waitingFor: combinedSearchString,
        tabId,
      });

    const apiEndpoint = `${BASE_URL}/api/Project/GetPaged?pageNo=${pageNo}&pageSize=${pageSize}&${qs.stringify(
      filters
    )}`;
    try {
      const res = await axios.get(apiEndpoint);
      dispatch({
        type: PROJECT.FETCH_ALL_PAGED_FULFILLED,
        tabId,
        waitingFor: combinedSearchString,
        projectList: res.data.projectList,
        resetList,
      });
      return res.data.pageCount;
    } catch (e) {
      raiseError(PROJECT.FETCH_ALL_REJECTED, e.response.data);
    }
  };

const fetchDetails = (projId) => async (dispatch) => {
  dispatch({ type: PROJECT.FETCH_DETAILS });
  const apiEndpoint = `${BASE_URL}/api/Project/${projId}`;
  try {
    const projDetailsRes = await axios.get(apiEndpoint);
    dispatch({
      type: PROJECT.FETCH_DETAILS_FULFILLED,
      projectId: projId,
      projDetails: projDetailsRes.data,
    });
    return;
  } catch (err) {
    raiseError(PROJECT.FETCH_DETAILS_REJECTED, err.response.data);
  }
};

const fetchJobStamp = (projectId) => async (dispatch, getState) => {
  const existJs = getState().projects.jobStamps[projectId];
  if (existJs) return existJs;

  dispatch({ type: PROJECT.FETCH_JOBSTAMP });
  const apiEndpoint = `${BASE_URL}/api/Project/${projectId}/JobStamp`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({
      type: PROJECT.FETCH_JOBSTAMP_FULFILLED,
      jobStamp: res.data,
      projectId,
    });
    return res.data;
  } catch (err) {
    raiseError(PROJECT.FETCH_JOBSTAMP_REJECTED, err);
  }
};

const fetchAllStaff = (projectId) => async (dispatch) => {
  dispatch({ type: PROJECT.FETCH_PROJECT_ASSIGNMENTS });
  const apiEndpoint = `${BASE_URL}/api/Project/${projectId}/AssignedStaff`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({
      type: PROJECT.FETCH_PROJECT_ASSIGNMENTS_FULFILLED,
      projectId,
      projStaffList: res.data,
    });
    return res.data;
  } catch (err) {
    raiseError(PROJECT.FETCH_PROJECT_ASSIGNMENTS_REJECTED, err.response.data);
  }
};

const fetchProjectAssignmentHistory = (projectId) => async (dispatch) => {
  dispatch({ type: PROJECT.FETCH_PROJECT_ASSIGNMENT_HISTORY });
  const apiEndpoint = `${BASE_URL}/api/Project/${projectId}/AssignmentHistory`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({
      type: PROJECT.FETCH_PROJECT_ASSIGNMENT_HISTORY_FULFILLED,
      projectId,
      projAssignmentHistoryList: res.data,
    });
  } catch (err) {
    raiseError(PROJECT.FETCH_PROJECT_ASSIGNMENT_HISTORY_REJECTED, err.response.data);
  }
};


/*
const fetchAssignedProjectsForUser = (userId) => async (dispatch) => {
  dispatch({ type: PROJECT.FETCH_ASSIGNED_PROJECTS_FOR_USER });
  const apiEndpoint = `${BASE_URL}/api/Project/List?${qs.stringify({userId})}`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({
      type: PROJECT.FETCH_ASSIGNED_PROJECTS_FOR_USER_FULFILLED,
      userId,
      //assignedProjectsForUser: res.data,
      payload: res.data,
    });
    return res.data;
  } catch (err) {
    raiseError(PROJECT.FETCH_ASSIGNED_PROJECTS_FOR_USER_REJECTED, err.response.data);
  }
};
*/

const fetchProjectTypes = () => async (dispatch) => {
  dispatch({ type: PROJECT.FETCH_TYPES });
  const baseUrl = `${BASE_URL}/api/ProjectTypes/List`;
  try {
    const res = await axios.get(baseUrl);
    dispatch({ type: PROJECT.FETCH_TYPES_FULFILLED, projTypes: res.data });
    return;
  } catch (e) {
    raiseError(PROJECT.FETCH_TYPES_REJECTED, e);
    return;
  }
};

const fetchProjectUnits = (projectId) => async (dispatch) => {
  dispatch({ type: PROJECT.FETCH_PROJECT_UNITS });
  const apiEndpoint = `${BASE_URL}/api/Project/${projectId}/Units`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({
      type: PROJECT.FETCH_PROJECT_UNITS_FULFILLED,
      projUnitList: res.data,
      projectId,
    });
    return res.data;
  } catch (err) {
    raiseError(PROJECT.FETCH_PROJECT_UNITS_REJECTED, err.response.data);
  }
};

/**
 * Saves changes to project units
 */
const saveProjectUnits = (puDtos, projectId) => async (dispatch) => {
  dispatch({ type: PROJECT.SAVE_PROJECT_UNITS });
  const apiEndpoint = `${BASE_URL}/api/Project/${projectId}/Units`;
  try {
    const res = await axios.put(apiEndpoint, puDtos);
    if (res.data.message) {
      dispatch({ type: PROJECT.SAVE_PROJECT_UNITS_REJECTED });
      raiseWarning(PROJECT.SAVE_PROJECT_UNITS_REJECTED, res.data.message);
    } else {
      dispatch({ type: PROJECT.SAVE_PROJECT_UNITS_FULFILLED });
    }
  } catch (err) {
    raiseError(PROJECT.SAVE_PROJECT_UNITS_REJECTED, err.response.data);
  }
};

const fetchProjectRoles = () => async (dispatch) => {
  dispatch({ type: PROJECT.FETCH_PROJECT_ROLES });
  const apiEndpoint = BASE_URL + "/api/ProjectRole/List";
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({
      type: PROJECT.FETCH_PROJECT_ROLES_FULFILLED,
      payload: res.data,
    });
  } catch (err) {
    raiseError(PROJECT.FETCH_PROJECT_ROLES_REJECTED, err.response.data);
  }
};

const saveProjectAssignments = (paDtos, projectId) => async (dispatch) => {
  dispatch({ type: PROJECT.SAVE_PROJECT_ASSIGNMENTS });
  const apiEndpoint = `${BASE_URL}/api/Project/${projectId}/Assignments`;
  try {
    const res = await axios.put(apiEndpoint, paDtos);
    if (res.data.message) {
      dispatch({ type: PROJECT.SAVE_PROJECT_ASSIGNMENTS_REJECTED });
      raiseWarning(PROJECT.SAVE_PROJECT_ASSIGNMENTS_REJECTED, res.data.message);
    } else {
      dispatch({ type: PROJECT.SAVE_PROJECT_ASSIGNMENTS_FULFILLED });
    }
  } catch (err) {
    raiseError(PROJECT.SAVE_PROJECT_ASSIGNMENTS_REJECTED, err.response.data);
  }
};

const getContractorDetails = (projectId, contractorId) => async (dispatch) => {
  dispatch({ type: CONTRACTOR.FETCH_PROJECT_CONTRACTOR_DETAILS });
  const apiEndpoint = `${BASE_URL}/api/Project/${projectId}/Contractor/${contractorId}`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({
      type: CONTRACTOR.FETCH_PROJECT_CONTRACTOR_DETAILS_FULFILLED,
      projectId,
      contractorId,
      payload: res.data,
    });
    return;
  } catch (e) {
    raiseError(CONTRACTOR.FETCH_PROJECT_CONTRACTOR_DETAILS_REJECTED, e.response.data);
  }
};

const contractorHasLinks = (projectId, contractorId) => async (dispatch) => {
  dispatch({ type: PROJECT.GENERIC_REQUEST });
  const apiEndpoint = `${BASE_URL}/api/Project/${projectId}/Contractor/${contractorId}/HasLinks`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({ type: PROJECT.GENERIC_REQUEST_FULFILLED });
    return res.data;
  } catch (e) {
    raiseError(PROJECT.GENERIC_REQUEST_REJECTED, e.response.data);
  }
};

const hasPendingData = (projectId) => async (dispatch) => {
  dispatch({ type: PROJECT.GENERIC_REQUEST });
  const apiEndpoint = `${BASE_URL}/api/NativeClient/Project/HasPendingData/${projectId}`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({ type: PROJECT.GENERIC_REQUEST_FULFILLED });
    return res.data;
  } catch (e) {
    raiseError(PROJECT.GENERIC_REQUEST_REJECTED, e.response.data);
  }
};

const getPendingData = (projectId) => async (dispatch) => {
  dispatch({ type: PROJECT.GENERIC_REQUEST });
  const apiEndpoint = `${BASE_URL}/api/NativeClient/PendingData/${projectId}`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({ type: PROJECT.GENERIC_REQUEST_FULFILLED });
    return res.data;
  } catch (e) {
    raiseError(PROJECT.GENERIC_REQUEST_REJECTED, e.response.data);
  }
};

const updatePendingData = (projectId, pendingDataForCaseB) => async (dispatch) => {
  dispatch({ type: PROJECT.GENERIC_REQUEST_FULFILLED });
  const apiEndpoint = `${BASE_URL}/api/NativeClient/ValidatePendingDataAndCommitDailyReports/${projectId}`;
  try {
    const res = await axios.put(apiEndpoint, pendingDataForCaseB);
    dispatch({ type: PROJECT.GENERIC_REQUEST_FULFILLED });
    return res.data;
  } catch (e) {
    raiseError(PROJECT.GENERIC_REQUEST_REJECTED, e.response.data);
  }
};

const emptyProjectFilters = {
  projectId: null,
  district: null,
  unitId: null,
  userId: null,
  EA: "",
  proDMSProjectsOnly: false,
};

// Keep this interface alphabetical
const projActions = {
  activate,
  add,
  contractorHasLinks,
  fetchAllByFilter,
  fetchAllPaged,
  fetchAllStaff,
  //fetchAssignedProjectsForUser,
  fetchDetails,
  fetchJobStamp,
  fetchProjectAssignmentHistory,
  fetchProjectUnits,
  fetchProjectRoles,
  fetchProjectTypes,
  getContractorDetails,
  getPendingData,
  hasPendingData,
  refetchAllOpenProjectLists,
  saveProjectUnits,
  saveProjectAssignments,
  update,
  updatePendingData,
};

export default projActions;
